import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { parseError } from '@/lib/errors'
import {
  type CorgiSendOutcome,
  endorsementSteps,
  type InternalEndorsement,
  useEndorsementStep,
} from '@/models/internalEndorsement'

/**
 * Sending the request on to the insurance companies. Cai drafts the changes on the Corgi portal itself as part
 * of the step; every other insurance company staff email first and confirm here.
 */
export function MarkSentDialog({
  endorsement,
  disabled,
}: {
  endorsement: InternalEndorsement
  disabled: boolean
}) {
  const send = useEndorsementStep(endorsementSteps.sendToCarriers)
  const [open, setOpen] = useState(false)

  const corgiCount = endorsement.policies.filter((policy) => policy.corgiEligible).length
  const othersCount = endorsement.policies.length - corgiCount

  async function confirm() {
    try {
      const sent = await send.mutateAsync(endorsement.id)
      reportCorgi(sent.corgiSend ?? [])
      if (othersCount > 0 || corgiCount === 0) {
        toast.success('Marked sent. Enter each insurance company’s quote here when it replies.')
      }
      setOpen(false)
    } catch (error) {
      toast.error(parseError(error))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" disabled={disabled}>
          {corgiCount > 0 ? 'Send to carriers' : 'Mark sent to carriers'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {corgiCount > 0
              ? 'Send it to the insurance companies?'
              : 'Did you send it to each insurance company?'}
          </DialogTitle>
          <DialogDescription>{description(corgiCount, othersCount)}</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-1 text-sm">
          {endorsement.policies.map((policy) => (
            <li key={policy.id}>
              <span className="font-medium">{policy.carrierName || 'Insurance company not on file'}</span>
              <span className="text-neutral-600">
                {' '}
                · {[policy.policyNumber, policy.coverages.join(', ')].filter(Boolean).join(' · ')}
              </span>
              {corgiCount > 0 && (
                <span className="block text-xs text-neutral-500">
                  {policy.corgiEligible
                    ? 'Cai drafts this one on the Corgi portal.'
                    : 'Email this one yourself first.'}
                </span>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void confirm()}
            loading={send.isPending ? (corgiCount > 0 ? 'Sending…' : 'Saving…') : undefined}
          >
            {confirmLabel(corgiCount, othersCount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function description(corgiCount: number, othersCount: number): string {
  if (corgiCount === 0) {
    return 'Cai doesn’t contact insurance companies. Email each one below the changes yourself first: once you mark it sent, the customer sees it went to their insurance providers.'
  }
  if (othersCount === 0) {
    return 'Cai drafts the trucks, trailers and drivers on the customer’s Corgi policies the moment you confirm. On the customer’s behalf it confirms Corgi’s three statements: no incidents that could give rise to a claim since the policy started, no claims filed or notice given, and no material change to the business. Nothing is submitted for a quote yet.'
  }
  return 'Cai drafts the changes on the Corgi policies itself, confirming Corgi’s three statements on the customer’s behalf: no incidents that could give rise to a claim, no claims filed or notice given, no material change to the business. Email the other insurance companies below the changes yourself first.'
}

function confirmLabel(corgiCount: number, othersCount: number): string {
  if (corgiCount === 0) return 'I sent it'
  return othersCount === 0 ? 'Send to Corgi' : 'I sent the others'
}

function reportCorgi(outcomes: CorgiSendOutcome[]) {
  for (const outcome of outcomes) {
    if (outcome.error) {
      toast.error(`Corgi, ${outcome.policyNumber}: ${outcome.error}`)
    } else if (outcome.added.length === 0) {
      toast.warning(
        `Nothing went on Corgi’s draft for ${outcome.policyNumber}; see what to add in the portal.`
      )
    } else {
      const left = outcome.leftForStaff.length
      toast.success(
        `Drafted on Corgi for ${outcome.policyNumber}: ${outcome.added.length} on the draft${
          left ? `, ${left} to add in the portal` : ''
        }. Submit it to Corgi when ready.`
      )
    }
  }
}
